import { Button, Card, Row, Space, Tabs, Tooltip, Tour } from "antd";
import React, { useContext, useEffect, useRef, useState } from "react";
import { ColumnComponent } from "../../components/ui";
import Table from "antd/es/table";
import DataContext from "../../context/DataContext";
import { useParams } from "react-router-dom";
import AuthContext from "../../context/AuthContext";
import { DownloadOutlined, FolderAddOutlined } from "@ant-design/icons";
import { SearchInput } from "../../components/functional";
import { ProjectTemplateProps } from "../../interfaces/interfaces";
import { useDemoModal } from "../../hooks";
import { exportProjectTemplateList } from "../../utils/func/ExportFile";
import {
  MODAL_INITIAL_STATE,
  PROJECT_TEMPLATE_INITIAL_STATE,
} from "../../utils/data";
import {
  ModalCreateOrUpdateProjectTemplate,
  ProjectTemplateList,
} from "../../components/project-template";
import { TabsProps } from "antd/lib";

export const ProjectTemplateListPage = () => {
  const { idClient } = useParams();
  const { clientInfo, isDemoEnv } = useContext(AuthContext);
  const { DemoModal, openModalDemo } = useDemoModal();
  const [activeTab, setActiveTab] = useState("1");
  const {
    getData,
    projectTemplateList,
    usersList,
    handleTour,
    handleCreateData,
    handleEditData,
  } = useContext(DataContext);
  const [selectedRowsToEdit, setSelectedRowToEdit] = useState<React.Key[]>([]);
  const [modalStatus, setModalStatus] = useState<{
    open: boolean;
    create: boolean;
    update: boolean;
    payload?: ProjectTemplateProps | null;
  }>(MODAL_INITIAL_STATE);
  const [idTemplateSelected, setIdTemplateSelected] = useState<string | null>(
    null,
  );
  const ref1 = useRef(null);

  useEffect(() => {
    if (idClient) {
      getData("project-templates", idClient, undefined, { active: true });
      getData("users", idClient);
    }
  }, [idClient]);

  useEffect(() => {
    if (isDemoEnv) {
      let tourOpened = localStorage.getItem("tour-summary");
      if (!tourOpened) {
        handleTour(true);
        localStorage.setItem("tour-customer", "true");
      }
    }
  }, []);

  useEffect(() => {
    if (projectTemplateList.length > 0 && idTemplateSelected) {
      const selectedTemplate = projectTemplateList.find(
        (template) => template._id === idTemplateSelected,
      );
      if (selectedTemplate) {
        setModalStatus({
          open: true,
          create: false,
          update: true,
          payload: selectedTemplate,
        });
      }
    }
  }, [projectTemplateList, idTemplateSelected]);

  const onChangeSelectedRowsToEdit = (newSelectedRowToEdit: React.Key[]) => {
    setSelectedRowToEdit(newSelectedRowToEdit);
  };

  const rowSelection = {
    selectedRowKeys: selectedRowsToEdit,
    onChange: onChangeSelectedRowsToEdit,
    selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
  };

  const createOrUpdateProjectTemplate = (
    values: ProjectTemplateProps,
    idClient: string,
    idProjectTemplate?: string,
    next?: () => void,
  ) => {
    if (modalStatus.create) {
      handleCreateData({
        endpoint: "project-templates",
        idClient,
        data: values,
        next: () => {
          if (next) {
            next();
            return;
          }
          getData("project-templates", idClient, undefined, {
            active: activeTab === "1",
          });
        },
      });
    } else if (modalStatus.update && idProjectTemplate) {
      handleEditData({
        endpoint: `project-template/${idClient}/${idProjectTemplate}`,
        newData: values,
        next: () => {
          if (next) {
            next();
            return;
          }
          getData("project-templates", idClient, undefined, {
            active: activeTab === "1",
          });
        },
        showAlert: true,
      });
    }
  };

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "Active",
      children: (
        <ProjectTemplateList
          projectTemplateList={projectTemplateList}
          onSelectOne={(project) => {
            setIdTemplateSelected(project._id);
            setModalStatus({
              open: true,
              create: false,
              update: true,
              payload: project,
            });
          }}
          rowSelection={rowSelection}
        />
      ),
    },
    {
      key: "2",
      label: "Archived",
      children: (
        <ProjectTemplateList
          projectTemplateList={projectTemplateList}
          onSelectOne={(project) => {
            setIdTemplateSelected(project._id);
            setModalStatus({
              open: true,
              create: false,
              update: true,
              payload: project,
            });
          }}
          rowSelection={rowSelection}
        />
      ),
    },
  ];

  const onChange = (key: string) => {
    setActiveTab(key);
    if (key === "1") {
      idClient &&
        getData("project-templates", idClient, undefined, {
          active: true,
        });
    } else if (key === "2") {
      idClient &&
        getData("project-templates", idClient, undefined, {
          active: false,
        });
    }
  };

  return (
    <>
      <Row gutter={[24, 0]}>
        <ColumnComponent>
          <div ref={ref1}>
            <Card
              className="summary-client__card"
              styles={{ body: { display: "none" } }}
              actions={[
                <></>,
                <Space direction="vertical" size={0} style={{ width: "100%" }}>
                  <label>Search</label>
                  <SearchInput
                    onSearch={(value: string) => {
                      if (value) {
                        idClient &&
                          getData("project-templates", idClient, value, {
                            active: activeTab === "1",
                          });
                      } else {
                        idClient &&
                          getData("project-templates", idClient, undefined, {
                            active: activeTab === "1",
                          });
                      }
                    }}
                  />
                </Space>,
                <Space direction="vertical" size={0} style={{ width: "100%" }}>
                  <label>Actions</label>
                  <Space direction="horizontal" size={0}>
                    <Tooltip title="Create new Template" placement="left">
                      <Button
                        type="link"
                        icon={<FolderAddOutlined />}
                        size="large"
                        onClick={() =>
                          setModalStatus({
                            open: true,
                            create: true,
                            update: false,
                            payload: PROJECT_TEMPLATE_INITIAL_STATE,
                          })
                        }
                      />
                    </Tooltip>
                    <Tooltip
                      title={
                        selectedRowsToEdit.length > 0
                          ? "Export Selected Data"
                          : "Export Data"
                      }
                      placement="left"
                    >
                      <Button
                        type="link"
                        icon={<DownloadOutlined />}
                        size="large"
                        onClick={() => {
                          exportProjectTemplateList(
                            projectTemplateList,
                            selectedRowsToEdit as string[],
                          );
                        }}
                      />
                    </Tooltip>
                  </Space>
                </Space>,
              ]}
            ></Card>
          </div>
        </ColumnComponent>
        <ColumnComponent>
          <Tabs
            defaultActiveKey="1"
            items={items}
            onChange={onChange}
            activeKey={activeTab}
            style={{ padding: "0 12px" }}
          />
        </ColumnComponent>
      </Row>

      <ModalCreateOrUpdateProjectTemplate
        modalStatus={modalStatus}
        idClient={idClient || ""}
        onSubmit={(values) => {
          if (isDemoEnv) {
            openModalDemo();
          } else {
            if (clientInfo && idClient) {
              createOrUpdateProjectTemplate(
                values,
                idClient,
                values._id,
                undefined,
              );
              setModalStatus(MODAL_INITIAL_STATE);
              setIdTemplateSelected(null);
            }
          }
        }}
        handleCreateProjectStatus={(
          values: ProjectTemplateProps,
          projectStatusName: string,
        ) => {
          idClient &&
            createOrUpdateProjectTemplate(values, idClient, values._id, () => {
              handleEditData({
                endpoint: `project-template/${idClient}/${modalStatus.payload?._id}`,
                newData: {
                  newProjectStatus: projectStatusName,
                },
                next: () => {
                  idClient &&
                    getData("project-templates", idClient, undefined, {
                      active: activeTab === "1",
                    });
                },
                showAlert: false,
              });
            });
        }}
        handleEditProjectStatus={(projectStatusId: string, next) => {
          idClient &&
            handleEditData({
              endpoint: `project-template/${idClient}/${modalStatus.payload?._id}`,
              newData: {
                statusDeleted: projectStatusId,
              },
              next: () => {
                next?.();
                idClient &&
                  getData("project-templates", idClient, undefined, {
                    active: activeTab === "1",
                  });
              },
              showAlert: true,
            });
        }}
        onCancel={() => {
          setModalStatus(MODAL_INITIAL_STATE);
        }}
      />

      <DemoModal />
    </>
  );
};
